import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Popover,
  Snackbar,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { usePostHog } from "posthog-js/react";
import { ReactComponent as InfoIcon } from "../../assets/icons/About.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/Download-Dark.svg";
import { ReactComponent as NewChat } from "../../assets/icons/NewChat-Dark.svg";
import { ReactComponent as FolderIcon } from "../../assets/icons/Folder-Dark.svg";
import { useChat } from "../../hooks/useChat";
import type { ChatMode, Prompt } from "../../types/frontend";
import { ChatModeValues } from "../../types/frontend";
import { useChatContext } from "../../contexts/ChatContext";
import { useSourceContext } from "../../contexts/SourcesContext";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import StarterPage from "./StarterPage";
import { panelStyles, chatPanelStyles } from "../PanelStyles";
import { FeedbackReaction, MessageRoles } from "../../types/frontend";
import { DOCUMENTATION_URL } from "../../constants";
import MessageActions from "./MessageActions";
import FeedbackPopover from "./FeedbackPopover";
import SavedChatsSidebar from "./SavedChatsSidebar";
import { isStarterPromptInCategory } from "../../hooks/useStarterPrompts";
import { PromptCategory } from "../../types/frontend";
import { parseLinksFromMarkdown } from "../../utils/markdownParser";

/**
 * Props interface for the ChatPanel component
 * @interface ChatPanelProps
 * @property {string} input - Current chat query text
 * @property {(query: string) => void} setInput - Callback function to update the chat query
 * @property {ChatMode} mode - Chat mode: "workflow" for grounded sources, "agent" for agent endpoint
 * @property {string} title - Optional custom title for the chat panel header
 * @property {string} starterTitle - Optional custom title for the starter page
 * @property {string} starterSubtitle - Optional custom subtitle for the starter page
 * @property {Prompt[]} starterPrompts - Optional custom starter prompts
 * @property {string} documentationUrl - Optional URL for the help/documentation link
 */
export interface ChatPanelProps {
  input: string;
  setInput: (query: string) => void;
  mode?: ChatMode;
  title?: string;
  starterTitle?: string;
  starterSubtitle?: string;
  starterPrompts?: Prompt[];
  documentationUrl?: string;
}

/**
 * ChatPanel component that handles the display and interaction of chat messages.
 * @param {ChatPanelProps} props - The properties for the ChatPanel component.
 * @returns {JSX.Element} The rendered ChatPanel component.
 */
const ChatPanel: React.FC<ChatPanelProps> = ({
  input,
  setInput,
  mode = ChatModeValues.Workflow,
  title = "Chat",
  starterTitle,
  starterSubtitle,
  starterPrompts,
  documentationUrl = DOCUMENTATION_URL,
}) => {
  const theme = useTheme();

  // Context hooks to manage chat and source states
  const {
    messages: storeMessages,
    addMessage,
    clearMessages,
  } = useChatContext();

  // Always call useSourceContext (requires SourceProvider wrapper)
  // For v2, the values are available but source-related UI is hidden
  const {
    clearSources,
    attachedSources,
    setAttachedSources,
    clearAttachedSources,
  } = useSourceContext();

  const { isLoading, error, sendMessage, abortGeneration, sendFeedback } =
    useChat({ mode });

  const [, setSearchParams] = useSearchParams();

  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [isSavedChatsOpen, setIsSavedChatsOpen] = useState(false);
  const [isSearchOn, setIsSearchOn] = useState(true);
  const [previousSearchState, setPreviousSearchState] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedReaction, setSelectedReaction] =
    useState<FeedbackReaction | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? "feedback-popover" : undefined;

  const messageListRef = useRef<HTMLUListElement>(null);
  const wasLoadingRef = useRef(false);

  // PostHog hook to track in-text citations
  const posthog = usePostHog();

  /**
   * Track when a message generation completes and log citation links to PostHog.
   */
  useEffect(() => {
    // Check if loading just finished (was loading, now not loading)
    if (wasLoadingRef.current && !isLoading) {
      // Get the last message
      const lastMessage = storeMessages[storeMessages.length - 1];

      // If the last message is an assistant message and has content
      if (
        lastMessage &&
        lastMessage.role === MessageRoles.Assistant &&
        lastMessage.content
      ) {
        const links = parseLinksFromMarkdown(lastMessage.content);

        // Capture each in-text citation to PostHog
        if (links.length > 0) {
          links.forEach((link) => {
            posthog.capture("intextCitationServed", {
              citation_url: link.url,
              citation_title: link.text,
              trace_id: lastMessage.traceId,
              mode,
            });
          });
        }
      }
    }

    wasLoadingRef.current = isLoading;
  }, [isLoading, storeMessages, posthog, mode]);

  /**
   * Determines whether to search for sources based on the message content.
   * Currently, we control this only by:
   * - Setting search off if the message is included in the "about chatbot" category of starter prompts.
   * Otherwise, we use the search toggle setting.
   */
  const shouldSearchForSources = (messageContent: string): boolean => {
    if (
      isStarterPromptInCategory(
        messageContent.trim(),
        PromptCategory.AboutChatbot
      )
    ) {
      return false;
    }
    return isSearchOn;
  };

  /**
   * Copies the provided text to the clipboard and shows a snackbar notification.
   * @param {string} content - The text content to be copied.
   */
  const handleCopyText = (content: string) => {
    navigator.clipboard.writeText(content);
    setSnackbarOpen(true);
  };

  /**
   * Closes the snackbar notification.
   * @param {React.SyntheticEvent | Event} [event] - The event that triggered the close.
   * @param {string} [reason] - The reason for closing the snackbar.
   */
  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  /**
   * Handles the submission of feedback for a chat message.
   *
   * @param traceId - The trace ID associated with the chat message.
   * @param feedback - The feedback to submit.
   */
  const handleSubmitFeedback = (
    traceId: string,
    reaction: FeedbackReaction,
    comment?: string,
    reasons?: string[]
  ) => {
    sendFeedback(traceId, {
      trace_id: traceId,
      score: reaction === FeedbackReaction.Up ? 1.0 : 0.0,
      reasons: reasons ?? [],
      comment: comment ?? "",
    });
  };

  /**
   * Handles the click event for the thumbs up/down feedback buttons on a chat message.
   *
   * If the same reaction is clicked again, it deselects the reaction and closes the feedback popover.
   * If a different reaction is clicked, it sets the selected reaction and opens the feedback popover anchored to the clicked element.
   *
   * @param event - The mouse event triggered by clicking the feedback button.
   * @param reaction - The type of reaction (FeedbackReaction.Up for thumbs up, FeedbackReaction.Down for thumbs down).
   * @param traceId - The trace ID associated with the chat message.
   */
  const handleThumbClick = (
    event: React.MouseEvent<HTMLElement>,
    reaction: FeedbackReaction,
    traceId: string
  ) => {
    // If the same reaction is clicked again,
    // deselect the reaction and close the feedback popover.
    if (selectedReaction === reaction) {
      setSelectedReaction(null);
      setAnchorEl(null);
    } else {
      // If a different reaction is clicked,
      // set the selected reaction and open the feedback
      // popover anchored to the clicked element, finally
      // submit the feedback to the backend.
      setSelectedReaction(reaction);
      setAnchorEl(event.currentTarget);
      handleSubmitFeedback(traceId, reaction, "", []);
    }
  };

  /**
   * Closes the feedback popover and resets the selected reaction.
   */
  const handleCloseFeedbackPopover = () => {
    setAnchorEl(null);
    setSelectedReaction(null);
  };

  // Scrolls to the bottom of the message list whenever a new message is added
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [storeMessages.length]);

  // Auto-toggle search based on attached sources
  useEffect(() => {
    if (attachedSources.length > 0) {
      // Store current search state before turning it off
      setIsSearchOn(false);
    } else if (attachedSources.length === 0) {
      // Restore previous search state when no sources are attached
      setIsSearchOn(previousSearchState);
    }
  }, [attachedSources, previousSearchState]);

  /**
   * Sends a message if the input is not empty and clears the input and attached sources.
   */
  const handleSendMessage = () => {
    if (input.trim()) {
      const messageWithTraceId = addMessage({
        role: MessageRoles.User,
        content: input,
        sources: attachedSources,
      });
      sendMessage(
        messageWithTraceId,
        storeMessages,
        shouldSearchForSources(input)
      );
      setInput("");
    }
  };

  /**
   * Opens the modal to start a new chat.
   */
  const handleOpenNewChatModal = () => {
    setIsNewChatModalOpen(true);
  };

  /**
   * Closes the new chat modal.
   */
  const handleCloseNewChatModal = () => {
    setIsNewChatModalOpen(false);
  };

  /**
   * Clears the current chat and sources, and closes the new chat modal.
   * Also resets the sessionId URL parameter to start a fresh session.
   */
  const handleStartNewChat = () => {
    clearMessages();
    clearSources();
    clearAttachedSources();

    // Reset sessionId URL parameter
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("sessionId");
        return newParams;
      },
      { replace: true }
    );

    setIsNewChatModalOpen(false);
  };

  /**
   * Sends a starter prompt as a new message.
   * @param {string} promptText - The starter prompt text.
   */
  const handleStarterPromptSend = (promptText: string) => {
    if (promptText.trim()) {
      const messageWithTraceId = addMessage({
        role: MessageRoles.User,
        content: promptText,
      });
      sendMessage(
        messageWithTraceId,
        storeMessages,
        shouldSearchForSources(promptText)
      );
      setInput("");
    }
  };

  /**
   * Opens the documentation in a new browser tab.
   */
  const handleOpenDocumentation = () => {
    window.open(documentationUrl, "_blank", "noopener");
  };

  /**
   * Toggles the search state and updates the previous search state.
   */
  const handleToggleSearch = () => {
    setIsSearchOn((prev) => !prev);
    setPreviousSearchState((prev) => !prev);
  };

  /**
   * Removes a source from the attached sources list by its ID.
   * @param {string} sourceId - The ID of the source to be removed.
   */
  const handleRemoveSource = (sourceId: string) => {
    setAttachedSources((prev) =>
      prev.filter((source) => source.id !== sourceId)
    );
  };

  /**
   * Placeholder function for downloading chat functionality.
   */
  const handleDownloadChat = () => {
    // TODO: Implement chat download functionality
  };

  // Check if the chat has started by verifying if there are any messages
  const hasStartedChat = storeMessages.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{ ...panelStyles.container, position: "relative" }}
    >
      {/* Chat content */}
      <Box sx={chatPanelStyles.contentContainer}>
        {/* Header */}
        <Box sx={panelStyles.header}>
          <Typography variant="h6" sx={panelStyles.headerTitle}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="New chat" placement="top">
              <IconButton
                size="small"
                onClick={handleOpenNewChatModal}
                data-attr="new-chat-button"
                sx={{
                  color: "text.secondary",
                  bgcolor: "grey.100",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  marginRight: 2,
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: "grey.200",
                  },
                }}
                disableRipple
              >
                <NewChat fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                <React.Fragment>
                  <div style={{ textAlign: "center" }}>
                    Coming soon:
                    <br />
                    Download chat
                  </div>
                </React.Fragment>
              }
              placement="top"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownloadChat}
                  data-attr="download-chat-button"
                  sx={{
                    color: "text.secondary",
                    bgcolor: "grey.100",
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    marginRight: 2,
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "grey.200",
                    },
                  }}
                  disableRipple
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Help" placement="top">
              <IconButton
                size="medium"
                onClick={handleOpenDocumentation}
                data-attr="help-button"
                sx={{
                  color: "text.secondary",
                  bgcolor: "grey.100",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  marginRight: 2,
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: "grey.200",
                  },
                }}
                disableRipple
              >
                <InfoIcon style={{ color: "grey.200" }} />
              </IconButton>
            </Tooltip>
            {mode === ChatModeValues.Agent && (
              <Button
                size="small"
                onClick={() => setIsSavedChatsOpen(true)}
                data-attr="saved-chats-button"
                startIcon={<FolderIcon />}
                sx={{
                  color: "primary.main",
                  bgcolor: "grey.200",
                  height: 36,
                  width: { xs: 36, sm: "auto" },
                  minWidth: { xs: 36, sm: "auto" },
                  borderRadius: { xs: "50%", sm: "18px" },
                  px: { xs: 0, sm: 2 },
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: "grey.300",
                  },
                  "& .MuiButton-startIcon": {
                    margin: { xs: 0, sm: "0 8px 0 -4px" },
                  },
                }}
                disableRipple
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  Saved Chats
                </Box>
              </Button>
            )}
          </Box>
        </Box>

        {/* Starter page */}
        {!hasStartedChat && (
          <StarterPage
            onPromptSend={handleStarterPromptSend}
            title={starterTitle}
            subtitle={starterSubtitle}
            starterPrompts={starterPrompts}
            documentationUrl={documentationUrl}
          />
        )}

        {/* Message list */}
        {hasStartedChat && (
          <List
            sx={{
              ...chatPanelStyles.messageList,
              overflowX: "hidden",
              overflowY: "visible",
            }}
            ref={messageListRef}
          >
            {storeMessages.map((chat, index) => {
              const isAssistant = chat.role === "assistant";
              const isLastAssistantMessage =
                isAssistant && index === storeMessages.length - 1;
              const isActivelyResponding = isLastAssistantMessage && isLoading;
              const hasContentStarted = chat.content && chat.content.length > 0;
              const isShowingProgress =
                isActivelyResponding && !hasContentStarted;
              const messageBackgroundColor = isAssistant
                ? isShowingProgress
                  ? "transparent"
                  : "#eff6f4"
                : theme.palette.materiom.seaweed;

              const messageTextColor = isAssistant
                ? theme.palette.text.primary
                : theme.palette.primary.contrastText;

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isAssistant ? "flex-start" : "flex-end",
                    mb: 1,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isAssistant ? "row" : "row-reverse",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    {/* Assistant Avatar */}
                    {isAssistant && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mr: 1,
                        }}
                      >
                        <img
                          src="/materiom_logo.png"
                          alt="Materiom Logo"
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            display: "block",
                            padding: 5,
                            border: "2px solid rgb(236, 236, 236)",
                          }}
                        />
                      </Box>
                    )}
                    {/* Message Content */}
                    <ListItem
                      data-messageid={`message-${index}-role-${chat.role}`}
                      sx={{
                        ...chatPanelStyles.messageItem(chat.role),
                        justifyContent: isAssistant ? "flex-start" : "flex-end",
                        maxWidth: "100%",
                        bgcolor: messageBackgroundColor,
                        color: messageTextColor,
                      }}
                    >
                      {/* Message Content */}
                      <ChatMessage
                        chat={chat}
                        styles={chatPanelStyles}
                        isLastMessage={index === storeMessages.length - 1}
                        isLoading={isLoading}
                      />
                    </ListItem>
                  </Box>
                  {/* Message Actions */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: isAssistant ? "flex-start" : "flex-end",
                      ml: isAssistant ? "56px" : 0,
                      mt: isAssistant ? "-10px" : 0,
                    }}
                  >
                    <MessageActions
                      isAssistantMessage={chat.role === MessageRoles.Assistant}
                      isLoading={isLoading}
                      isLastMessage={index === storeMessages.length - 1}
                      selectedReaction={selectedReaction}
                      onCopy={() => handleCopyText(chat.content)}
                      onThumbClick={(event, reaction) =>
                        handleThumbClick(event, reaction, chat.traceId ?? "")
                      }
                      chatMode={mode}
                    />
                  </Box>
                  {/* Feedback Popover */}
                  {isPopoverOpen && (
                    <Popover
                      id={popoverId}
                      open={isPopoverOpen}
                      anchorEl={anchorEl}
                      onClose={handleCloseFeedbackPopover}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                      <FeedbackPopover
                        reaction={selectedReaction!}
                        score={
                          selectedReaction === FeedbackReaction.Up ? 1.0 : 0.0
                        }
                        traceId={chat.traceId ?? ""}
                        onClose={handleCloseFeedbackPopover}
                        handleSubmitFeedback={(
                          traceId,
                          reaction,
                          comment,
                          reasons
                        ) =>
                          handleSubmitFeedback(
                            traceId,
                            reaction,
                            comment,
                            reasons
                          )
                        }
                      />
                    </Popover>
                  )}
                  {/* Snackbar for copying text */}
                  <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  >
                    <Alert
                      onClose={handleSnackbarClose}
                      severity="success"
                      sx={{ width: "100%" }}
                    >
                      Response text copied to clipboard!
                    </Alert>
                  </Snackbar>
                </Box>
              );
            })}
          </List>
        )}
        {error && (
          <ListItem>
            <Alert
              severity="error"
              sx={{ width: "100%" }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => handleSendMessage()}
                  disableRipple
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          </ListItem>
        )}
      </Box>
      {/* Chat Input */}
      <Box sx={chatPanelStyles.inputContainer}>
        <ChatInput
          input={input}
          setInput={setInput}
          isLoading={isLoading}
          handleSendMessage={handleSendMessage}
          handleStopGeneration={abortGeneration}
          styles={chatPanelStyles}
          isSearchOn={mode === ChatModeValues.Agent ? false : isSearchOn}
          onToggleSearch={handleToggleSearch}
          attachedSources={mode === ChatModeValues.Agent ? [] : attachedSources}
          onRemoveSource={handleRemoveSource}
          hideSearchToggle={mode === ChatModeValues.Agent}
        />
      </Box>

      {/* Saved Chats Sidebar */}
      <SavedChatsSidebar
        isOpen={isSavedChatsOpen}
        onClose={() => setIsSavedChatsOpen(false)}
        isGenerating={isLoading}
        onDeleteCurrentChat={handleStartNewChat}
      />

      {/* New Chat Modal */}
      <Dialog
        open={isNewChatModalOpen}
        onClose={handleCloseNewChatModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="new-chat-dialog-title" sx={{ textAlign: "center" }}>
          Start a new chat
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "grey.600" }}>
            {mode === ChatModeValues.Agent
              ? "You can return to this conversation anytime from Saved Chats. Start a new chat?"
              : "This will clear your current chat history. Are you sure you want to continue?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={handleCloseNewChatModal}
            variant="outlined"
            disableRipple
            disableElevation
            sx={{
              px: 3,
              py: 1,
              borderColor: "grey.300",
              color: "text.primary",
              "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartNewChat}
            variant="contained"
            color="primary"
            disableRipple
            disableElevation
            sx={{ px: 3, py: 1 }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChatPanel;
